import React from 'react';
import {
  MapPin,
  BadgeCheck,
  CalendarClock,
  Wind,
  RotateCcw,
  Luggage,
  Globe,
  DoorOpen,
  Zap,
  Car,
} from 'lucide-react';

const ICON_MAP = {
  MapPin,
  BadgeCheck,
  CalendarClock,
  Wind,
  RotateCcw,
  Luggage,
  Globe,
  DoorOpen,
  Zap,
  Car,
};

/**
 * SEO-safe trust strip (react + lucide only).
 * Props: features, variant ('inline' | 'list'), size ('sm' | 'default'),
 * tone ('default' | 'light'), className, policyHref
 */
const ServiceTrustStrip = ({
  features = [],
  variant = 'inline',
  size = 'default',
  tone = 'default',
  className = '',
  policyHref = '/booking-conditions',
}) => {
  if (!features.length) return null;

  const isLight = tone === 'light';
  const iconSize = size === 'sm' ? 13 : 15;
  const labelClass =
    size === 'sm'
      ? `text-xs ${isLight ? 'text-white/75' : 'text-[#7A6E67]'}`
      : `text-sm ${isLight ? 'text-white/80' : 'text-[#7A6E67]'}`;
  const iconClass = isLight ? 'text-white/90' : 'text-[#A44432]';

  if (variant === 'list') {
    return (
      <ul className={`space-y-3 ${className}`}>
        {features.map((item) => {
          const Icon = ICON_MAP[item.icon];
          if (!Icon) return null;
          return (
            <li key={item.id} className="flex gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#A44432]/10 text-[#A44432]">
                <Icon size={16} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#2D1E17]">{item.label}</p>
                {item.detail && (
                  <p className="mt-0.5 text-xs text-[#7A6E67] leading-snug">
                    {item.detail}
                    {item.policyLink && policyHref && (
                      <>
                        {' '}
                        <a href={policyHref} className="text-[#A44432] hover:underline">
                          See policy
                        </a>
                      </>
                    )}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div
      className={`flex flex-wrap gap-x-6 gap-y-2 ${className}`}
      role="list"
      aria-label="Service highlights"
    >
      {features.map((item) => {
        const Icon = ICON_MAP[item.icon];
        if (!Icon) return null;
        return (
          <span key={item.id} className={`inline-flex items-center gap-2 ${labelClass}`} role="listitem">
            <Icon size={iconSize} className={`shrink-0 ${iconClass}`} aria-hidden="true" />
            {item.label}
          </span>
        );
      })}
    </div>
  );
};

export default ServiceTrustStrip;
